import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If provided, user must have at least one of these roles (admins always pass). */
  requiredRoles?: UserRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyRole } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-baker-red border-t-transparent rounded-full" />
      </div>
    );
  }

  // If not authenticated, redirect to login via SWA auth
  // Use window.location only if we're sure the user isn't authenticated
  if (!isAuthenticated) {
    // Don't redirect in a loop — check if we're already on the auth path
    if (!window.location.pathname.startsWith('/.auth')) {
      window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
    }
    return null;
  }

  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h1>
        <p className="text-gray-500 mb-4">You don't have permission to view this page.</p>
        <a href="/" className="text-baker-red hover:underline text-sm">Return to Dashboard</a>
      </div>
    );
  }

  return <>{children}</>;
}
